import { z } from "zod";

const DEMO_SSO_CONTEXT_KEY = "roster-demo-sso-context";

const demoSsoMemberIdSchema = z.string().trim().min(1);
const demoSsoRoleSchema = z.string().trim().min(1).nullable();

const selectingDemoSsoContextSchema = z
  .object({
    phase: z.literal("selecting"),
    memberid: demoSsoMemberIdSchema,
    activeRole: z.null(),
  })
  .strict();

const authenticatedDemoSsoContextSchema = z
  .object({
    phase: z.literal("authenticated"),
    memberid: demoSsoMemberIdSchema,
    activeRole: demoSsoRoleSchema,
  })
  .strict();

const demoSsoContextSchema = z.discriminatedUnion("phase", [
  selectingDemoSsoContextSchema,
  authenticatedDemoSsoContextSchema,
]);

const legacyDemoSsoContextSchema = z
  .object({
    memberid: demoSsoMemberIdSchema,
    activeRole: demoSsoRoleSchema,
  })
  .strict()
  .transform((context) => ({
    phase: "authenticated" as const,
    ...context,
  }));

const persistedDemoSsoContextSchema = z.union([
  demoSsoContextSchema,
  legacyDemoSsoContextSchema,
]);

export type DemoSsoContext = z.infer<typeof demoSsoContextSchema>;

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch (error) {
    if (error instanceof DOMException) return null;
    throw error;
  }
}

export function saveDemoSsoContext(context: DemoSsoContext): void {
  const parsed = demoSsoContextSchema.parse(context);
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(DEMO_SSO_CONTEXT_KEY, JSON.stringify(parsed));
  } catch (error) {
    if (error instanceof DOMException) return;
    throw error;
  }
}

export function readDemoSsoContext(): DemoSsoContext | null {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(DEMO_SSO_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = persistedDemoSsoContextSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data;

    storage.removeItem(DEMO_SSO_CONTEXT_KEY);
    return null;
  } catch (error) {
    if (error instanceof SyntaxError) {
      clearDemoSsoContext();
      return null;
    }
    if (error instanceof DOMException) return null;
    throw error;
  }
}

export function clearDemoSsoContext(): void {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.removeItem(DEMO_SSO_CONTEXT_KEY);
  } catch (error) {
    if (error instanceof DOMException) return;
    throw error;
  }
}

export function clearDemoSsoContextIfMatches(
  expected: DemoSsoContext
): void {
  const current = readDemoSsoContext();
  if (
    !current ||
    current.phase !== expected.phase ||
    current.memberid !== expected.memberid ||
    current.activeRole !== expected.activeRole
  ) {
    return;
  }

  clearDemoSsoContext();
}
